import React, { useState } from 'react';
import './selects.css';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const SelectForm = ({
  options,
  label,
  name,
  className,
  isRequired = false,
  placeholder,
  description,
  isDisabled = false,
  highlightValue = false,
  onChange,
  isSearchable = false, // Nueva prop opcional
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const { t } = useTranslation('translation', { keyPrefix: 'inputs' });
  const validationRules = {};

  if (isRequired) {
    validationRules.required = t('required');
  }

  const findLabelByValue = (value) => {
    const option = options?.find((o) => o.value === value);
    return option ? option : placeholder;
  };

  const filteredOptions = searchValue
    ? options?.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase()))
    : options;

  const onOpenChange = (open) => {
    setOpen(open);
    setSearchValue('');
  };

  return (
    <div className={`${className}`}>
      <FormField
        name={name}
        control={control}
        rules={validationRules}
        render={({ field }) => (
          <FormItem>
            {label ? <FormLabel>{label}</FormLabel> : null}

            {isSearchable ? (
              <Popover open={open} onOpenChange={onOpenChange}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant='outline'
                      role='combobox'
                      disabled={isDisabled}
                      aria-expanded={open}
                      className={cn(
                        'tw-w-full tw-justify-between tw-font-normal',
                        highlightValue && field.value ? 'tw-font-bold' : '',
                        'tw-flex tw-h-10 tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-border tw-border-zinc-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-ring-offset-white placeholder:tw-text-zinc-500 focus:tw-outline-none disabled:tw-cursor-not-allowed disabled:tw-opacity-50 disabled:tw-text-muted-foreground [&>span]:tw-line-clamp-1 dark:tw-border-zinc-800 dark:tw-bg-zinc-950 dark:tw-ring-offset-zinc-950 dark:placeholder:tw-text-zinc-400 dark:focus:tw-ring-zinc-300',
                        'hover:!tw-bg-white',
                        errors[name] ? '!tw-border-red-600' : '',
                      )}
                    >
                      {field.value ? (
                        <div className='text-truncate'>
                          {findLabelByValue(field.value)?.img ? (
                            <img
                              src={findLabelByValue(field.value)?.img}
                              alt={findLabelByValue(field.value)?.label}
                              className='tw-w-6 tw-h-6 tw-mr-2 tw-rounded-full tw-object-cover'
                            />
                          ) : null}
                          {findLabelByValue(field.value)?.label}
                          {findLabelByValue(field.value)?.description ? (
                            <span className='tw-text-muted-foreground tw-ml-2 tw-whitespace-nowrap'>
                              {findLabelByValue(field.value)?.description}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        placeholder
                      )}
                      <ChevronDown className='tw-h-4 tw-w-4 tw-opacity-50' />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className='tw-w-[380px] p-0'>
                  <Command shouldFilter={false}>
                    <CommandInput placeholder={t('search')} onValueChange={setSearchValue} />
                    <CommandList>
                      {filteredOptions?.length === 0 && (
                        <CommandEmpty>{t('noResults')}</CommandEmpty>
                      )}
                      <CommandGroup>
                        {filteredOptions?.map((option) => (
                          <CommandItem
                            key={option.value}
                            value={option.value}
                            onSelect={() => {
                              const value = option.value;
                              field.onChange(value);
                              if (onChange) onChange(value);
                              setOpen(false);
                              setSearchValue('');
                            }}
                          >
                            <Check
                              className={cn(
                                'tw-mr-2 tw-h-4 tw-w-4',
                                field.value === option.value ? 'tw-opacity-100' : 'tw-opacity-0',
                              )}
                            />
                            {option.img ? (
                              <img
                                src={option?.img}
                                alt={option?.label}
                                className='tw-w-8 tw-h-8 tw-rounded-full tw-object-cover'
                              />
                            ) : null}
                            {option.label}
                            {option.description ? (
                              <span className='tw-text-muted-foreground tw-whitespace-nowrap'>
                                {option.description}
                              </span>
                            ) : null}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <Select
                disabled={isDisabled}
                onValueChange={(value) => {
                  field.onChange(value);
                  if (onChange) onChange(value);
                }}
                value={field.value}
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger error={!!errors[name]}>
                    <div className={highlightValue ? 'tw-font-bold' : ''}>
                      <SelectValue placeholder={placeholder} />
                    </div>
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {options &&
                    options.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}{' '}
                        {o.description ? (
                          <span className='tw-text-muted-foreground'>{o.description}</span>
                        ) : null}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}

            {description ? <FormDescription>{description}</FormDescription> : null}
            {errors[name] && (
              <div className='d-flex align-items-center error-content'>
                <i className='fas fa-exclamation-triangle text-danger mr-2'></i>
                <p className='text-danger p-0 m-0'>{errors[name].message}</p>
              </div>
            )}
          </FormItem>
        )}
      />
    </div>
  );
};

export default SelectForm;
